import { useEffect } from 'react';
import { PAGE_SEO } from '../../config/seo';
import { useSEO } from '../../helpers/useSEO';
import Section from '../Section';
import AboutUsSection from '../Section/AboutSection';
import HeroSection from '../Section/HeroSection';
import MovingTextSection from '../Section/MovingTextSection';
import ServiceSection from '../Section/ServiceSection';
import TestimonialSection from '../Section/TestimonialSection';
import ContactSectionStyle2 from '../Section/ContactSection/ContactSectionStyle2';
import FaqSection from '../Section/FaqSection';

const heroData = {
  bgUrl: '/images/hero_slider_1.png',
  titleUp: 'Nexting Études',
  title: "Construire l'avenir avec <br>précision et expertise",
  subTitle:
    'Nous mettons notre expertise au service de vos projets de construction, <br>de la conception à la réalisation, avec rigueur et innovation.',
  btnText: 'Demander une étude',
  btnUrl: '/contact',
};

const movingTextData = [
  '• INGÉNIERIE •',
  '• ÉTUDES TECHNIQUES •',
  '• SUIVI DE CHANTIER •',
  '• COORDINATION DE CHANTIER •',
  '• SUIVI OPÉRATIONNEL •',
];

const serviceData = [
  {
    iconUrl: '/images/icons/service_icon_1.svg',
    title: 'Études structurelles',
    subTitle:
      'Analyse et dimensionnement des structures pour garantir la solidité et la conformité de vos ouvrages.',
    imgUrl: '/images/service1.svg',
    href: '/service/services-details',
  },
  {
    iconUrl: '/images/icons/service_icon_2.svg',
    title: 'Suivi et pilotage de chantier',
    subTitle:
      'Supervision technique et organisationnelle pour assurer le bon déroulement de vos travaux.',
    imgUrl: '/images/project_1.jpeg',
    href: '/service/services-details',
  },
  {
    iconUrl: '/images/icons/service_icon_3.svg',
    title: "Études d'exécution",
    subTitle:
      'Plans détaillés et dossiers techniques pour une mise en œuvre précise sur le terrain.',
    imgUrl: '/images/service3.svg',
    href: '/service/services-details',
  },
  {
    iconUrl: '/images/icons/service_icon_4.svg',
    title: 'Réduction des coûts',
    subTitle:
      'Optimisation des solutions techniques pour maîtriser votre budget sans compromettre la qualité.',
    imgUrl: '/images/service4.svg',
    href: '/service/services-details',
  },
];

const testimonialData = [
  {
    imgUrl: '/images/avatar_1.png',
    rating: '5',
    desc: 'Une équipe très professionnelle et réactive. Les études techniques étaient précises et parfaitement adaptées à notre projet. Le suivi a été sérieux du début à la fin.',
    name: 'Nour Ben Youssef',
    designation: 'Client',
  },
  {
    imgUrl: '/images/avatar_2.png',
    rating: '5',
    desc: 'Excellent bureau d\'études. Les solutions proposées ont permis d\'optimiser les coûts tout en respectant les normes de construction. Une vraie valeur ajoutée.',
    name: 'Amine Trabelsi',
    designation: 'Client',
  },
  {
    imgUrl: '/images/avatar_3.png',
    rating: '5',
    desc: 'Un accompagnement rigoureux et un suivi de chantier impeccable. L\'équipe maîtrise parfaitement les aspects techniques et organisationnels.',
    name: 'Leila Bouzid',
    designation: 'Client',
  },
  {
    imgUrl: '/images/avatar_4.png',
    rating: '5',
    desc: 'Nexting Études a su comprendre nos besoins et proposer des solutions fiables. Nous recommandons leurs services sans hésitation.',
    name: 'Sami Jaziri',
    designation: 'Client',
  },
];

const faqData = [
  {
    title: 'Quels types de projets prenez-vous en charge ?',
    desc: 'Nous accompagnons les projets de construction neuve, de rénovation et d\'ingénierie du bâtiment : logements, bâtiments tertiaires, ouvrages techniques et aménagements divers, de l\'étude préliminaire au suivi de chantier.',
  },
  {
    title: 'Proposez-vous un suivi de chantier complet ?',
    desc: 'Oui. Nous assurons le pilotage technique et organisationnel du chantier, la coordination des intervenants et le contrôle de la conformité des travaux aux études validées.',
  },
  {
    title: 'Comment se déroule une demande d\'étude ?',
    desc: 'Après un premier échange sur votre projet, nous analysons vos besoins, définissons le périmètre de la mission et vous transmettons une proposition détaillée avec planning et budget.',
  },
  {
    title: 'Intervenez-vous sur toute la Tunisie ?',
    desc: 'Nous accompagnons nos clients sur l\'ensemble du territoire tunisien et pouvons adapter notre intervention selon la nature et la localisation de votre projet.',
  },
];

export default function Home() {
  useSEO(PAGE_SEO.home);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <HeroSection data={heroData} />

      <Section>
        <MovingTextSection data={movingTextData} />
      </Section>

      <Section
        pt="133"
        ptLg="75"
        pb="140"
        pbLg="80"
        className="cs_gray_bg cs_section_with_shape position-relative overflow-hidden"
      >
        <ServiceSection
          sectionTitleUp="Nos Services"
          sectionTitle="Notre bureau met son savoir-faire au service de projets maîtrisés."
          sectionBtnText=" Découvrir nos services"
          sectionBtnUrl="/service"
          data={serviceData}
        />
      </Section>

      <Section
        pt="135"
        ptLg="75"
        pb="140"
        pbLg="80"
        className="position-relative"
      >
        <AboutUsSection
          thumbUrl="/images/a_propos_1.svg"
          experienceBgUrl="/images/a_propos_2.svg"
          experienceIconUrl="/images/experience_icon.svg"
          experienceNumber="15"
          experienceTitle="Années d'expérience"
          sectionTitleUp="A Propos de nous"
          sectionTitle='Pourquoi choisir <span className="text-accent">Nexting Études</span> ?'
          sectionSubTitle="Spécialisés en ingénierie du bâtiment et en génie civil, nous mettons notre expertise technique au service de projets exigeants. Notre équipe combine rigueur, innovation et écoute pour garantir des solutions fiables et performantes.<br><br>De l'étude préliminaire au suivi opérationnel de chantier, nous vous accompagnons à chaque étape pour sécuriser vos décisions et optimiser la réussite de vos ouvrages."
          grothTitle="Expertise technique reconnue"
          grothPercentage="90"
          featureList={[
            {
              title: 'Accompagnement personnalisé',
              subTitle:
                'Un interlocuteur dédié et des solutions adaptées à chaque projet.',
            },
            {
              title: 'Conformité réglementaire',
              subTitle:
                'Des études conformes aux normes en vigueur et aux exigences du maître d\'ouvrage.',
            },
          ]}
          btnText="Demander une consultation"
          btnUrl="/contact"
        />
      </Section>
      <Section pb="140" pbLg="80" className="position-relative overflow-hidden">
        <FaqSection
          sectionTitleUp="FAQ"
          sectionTitle="Questions fréquentes"
          sectionSubTitle="Retrouvez les réponses aux questions les plus fréquentes sur nos prestations, notre méthode de travail et notre accompagnement."
          data={faqData}
          bgImgUrl="/images/faq.svg"
          imgUrl="/images/why_chose_1.jpeg"
          iconUrl="/images/icons/faq_icon_1.svg"
          featureTitle="Informations utiles sur nos services et notre accompagnement."
          featureList={[
            'Retrouvez les réponses à vos questions sur nos services.',
            'Tout ce qu\'il faut savoir sur nos prestations.',
          ]}
        />
      </Section>
      <Section
        pt="140"
        ptLg="75"
        pb="135"
        pbLg="75"
        className="cs_section_with_shape position-relative overflow-hidden"
        style={{ backgroundImage: `url(${'/images/testimonial_bg.jpeg'})` }}
      >
        <TestimonialSection
          sectionTitleUp="Témoignages clients"
          sectionTitle="Que disent-ils ?"
          sectionSubTitle="Les retours de nos clients reflètent notre engagement à fournir des études techniques fiables, précises et adaptées à chaque projet."
          data={testimonialData}
        />
      </Section>

   

      <Section
        pt="80"
        ptLg="75"
        pb="85"
        pbLg="55"
      >
        <ContactSectionStyle2
          sectionTitle='Donnez vie à vos projets avec <span className="text-accent">Nexting Études</span>'
          sectionTitleUp="Contact"
          sectionSubTitle="Vous avez un projet de construction, de rénovation ou d'ingénierie ? Notre équipe est à votre disposition pour vous accompagner avec des solutions techniques fiables et performantes."
          featureList={[
            'Demander un devis',
            'Planifier une consultation',
          ]}
          contactInfo={[
            {
              imgUrl: '/images/icons/contact_icon_1.svg',
              titleUp: 'Appelez-nous',
              title: '+216 00 000 000',
              textColor: 'text-primary',
            },
            {
              imgUrl: '/images/icons/contact_icon_2.svg',
              titleUp: 'Contactez-nous par email',
              title: 'Nexting@support.com',
              textColor: 'text-primary',
            },
          ]}
        />
      </Section>
    </>
  );
}
